/**
 * Composable for the XAUT (Tether Gold) USD price.
 *
 * Reads the latest price from public.exchange_rates via Supabase realtime
 * (populated every 5 minutes by the price-snapshot edge function). The
 * browser no longer calls Li.Fi or any third-party oracle directly.
 *
 * Public API is preserved exactly so the 30+ existing call sites do not need
 * to change.
 */
import { watch } from 'vue'
import * as Sentry from '@sentry/nuxt'
import { useExchangeRate } from './useExchangeRate'

const XAUT_ETHEREUM = '0x68749665ff8d2d112fa859aa293f07a622782f38'
// Used until the first DB read resolves (and as a last-resort if it fails).
const FALLBACK_PRICE_USDT = 4869
// Throttle Sentry warnings — at most one per cache window, matching the
// previous Li.Fi-based implementation.
const SENTRY_REPORT_THROTTLE_MS = 5 * 60 * 1000
let lastPriceErrorReportedAt = 0
let sentryWatcherInstalled = false

// Test-only — reset module state between specs.
export function __resetXautPriceStateForTests(): void {
  lastPriceErrorReportedAt = 0
  sentryWatcherInstalled = false
}

export function useXautPrice() {
  const { price: priceUSDT, isLoading, error, lastFetchedAt, refresh } =
    useExchangeRate('ethereum', XAUT_ETHEREUM, FALLBACK_PRICE_USDT)

  // Mirror the original Sentry behavior: warn on transition to error state,
  // throttled. Installed once per module load (state is shared across instances).
  if (!sentryWatcherInstalled) {
    sentryWatcherInstalled = true
    watch(error, (next) => {
      if (!next) return
      const now = Date.now()
      if (now - lastPriceErrorReportedAt < SENTRY_REPORT_THROTTLE_MS) return
      lastPriceErrorReportedAt = now
      Sentry.captureMessage('XAUT price fetch failed', { level: 'warning', tags: { flow: 'price' } })
    })
  }

  /** Manually re-fetch the latest price. By default this respects the 5-min
   *  TTL cache (so repeated mounts/refocus don't hit the REST endpoint); pass
   *  `force = true` to bypass the cache and read from the DB unconditionally. */
  async function refreshPrice(force = false): Promise<void> {
    await refresh(force)
  }

  /** Convert XAUT amount to USDT (1 USDT ≈ 1 USD). */
  function toUSDT(xautAmount: number | string): number {
    const amount = typeof xautAmount === 'string' ? parseFloat(xautAmount) : xautAmount
    if (isNaN(amount)) return 0
    return parseFloat(MathUtil.multiply(amount, priceUSDT.value))
  }

  /** Convert USDT amount to XAUT. */
  function toXAUT(usdtAmount: number | string): number {
    const amount = typeof usdtAmount === 'string' ? parseFloat(usdtAmount) : usdtAmount
    if (isNaN(amount) || priceUSDT.value === 0) return 0
    return parseFloat(MathUtil.divide(amount, priceUSDT.value))
  }

  /** Format an XAUT amount as a USD currency string. Non-zero sub-cent
   *  magnitudes render as `<$0.01` rather than `$0.00` — see `formatUsdAmount`. */
  function formatAsUSDT(xautAmount: number | string): string {
    return formatUsdAmount(toUSDT(xautAmount))
  }

  /** Convert coins (1 coin = 1 USDT) to oz of gold. */
  function coinsToOzGold(coins: number | string): string {
    return MathUtil.coinsToOzGold(coins, priceUSDT.value)
  }

  /** Convert XAUT to USD. The XAUT price is sourced from the priceOracle in
   *  USD per troy ounce, so this and `toUSDT` return the same value today —
   *  they remain distinct names so callers can express intent (USD vs USDT
   *  semantics may diverge once stablecoins are tracked separately). */
  function toUSD(xautAmount: number | string): number {
    return toUSDT(xautAmount)
  }

  /** Format an XAUT amount as a USD currency string. See `toUSD`. */
  function formatAsUSD(xautAmount: number | string): string {
    return formatAsUSDT(xautAmount)
  }

  return {
    price: priceUSDT,
    priceUSDT,
    isLoading,
    error,
    lastFetchedAt,
    refreshPrice,
    toUSDT,
    toXAUT,
    formatAsUSDT,
    coinsToOzGold,
    toUSD,
    formatAsUSD,
  }
}
